"use client";

import { useState, useRef, useEffect, useId, useCallback } from "react";

export function CustomSelect({
  id,
  value,
  onChange,
  options,
  label: ariaLabel,
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string; group?: string }>;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [triggerFocused, setTriggerFocused] = useState(false);
  const [optionFocusedIndex, setOptionFocusedIndex] = useState<number | null>(null);
  const [focusTriggerRequest, setFocusTriggerRequest] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const uid = useId();
  const listboxId = `${uid}-listbox`;
  const selectedIndex = options.findIndex((o) => o.value === value);
  const [activeIndex, setActiveIndex] = useState<number>(selectedIndex >= 0 ? selectedIndex : 0);
  const selectedLabel = options.find((o) => o.value === value)?.label ?? value;

  const hasGroups = options.some((o) => o.group != null);
  const groups: Array<{ label: string | null; items: Array<{ option: typeof options[number]; idx: number }> }> = [];
  if (hasGroups) {
    const seen = new Map<string, number>();
    options.forEach((o, idx) => {
      const g = o.group ?? "";
      if (!seen.has(g)) { seen.set(g, groups.length); groups.push({ label: g || null, items: [] }); }
      groups[seen.get(g)!].items.push({ option: o, idx });
    });
  }

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  useEffect(() => {
    if (!open || activeIndex < 0 || !listboxRef.current) return;
    const optionEl = listboxRef.current.querySelectorAll<HTMLElement>("[role='option']")[activeIndex];
    optionEl?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  useEffect(() => {
    if (open || focusTriggerRequest === 0) return;
    triggerRef.current?.focus();
  }, [focusTriggerRequest, open]);

  const openSelect = useCallback(() => {
    const idx = options.findIndex((o) => o.value === value);
    setActiveIndex(idx >= 0 ? idx : 0);
    setOpen(true);
  }, [options, value]);

  const closeAndFocusTrigger = useCallback(() => {
    setFocusTriggerRequest((request) => request + 1);
    setOpen(false);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen((o) => !o);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        openSelect();
      } else {
        setActiveIndex((i) => Math.min(i + 1, options.length - 1));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        openSelect();
      } else {
        setActiveIndex((i) => Math.max(i - 1, 0));
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeAndFocusTrigger();
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIndex(options.length - 1);
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  }, [closeAndFocusTrigger, open, openSelect, options.length]);

  const handleOptionKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>, idx: number) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onChange(options[idx].value);
      closeAndFocusTrigger();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeAndFocusTrigger();
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIndex(options.length - 1);
    }
  }, [closeAndFocusTrigger, onChange, options]);

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-label={ariaLabel}
        onClick={() => {
          if (open) setOpen(false);
          else openSelect();
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => setTriggerFocused(true)}
        onBlur={() => setTriggerFocused(false)}
        style={{
          width: "100%",
          minHeight: 44,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 6,
          borderRadius: 6,
          border: `1px solid ${open ? "#3b82f6" : "#334155"}`,
          background: "rgba(255,255,255,0.05)",
          padding: "7px 10px",
          fontSize: 13,
          color: "#e2e8f0",
          cursor: "pointer",
          textAlign: "start",
          userSelect: "none",
          boxSizing: "border-box",
          outline: (open || triggerFocused) ? "2px solid #2563eb" : "none",
          outlineOffset: 2,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
          {selectedLabel}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 20 20"
          fill="#94a3b8"
          aria-hidden="true"
          style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <div
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel}
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            insetInlineStart: 0,
            insetInlineEnd: 0,
            borderRadius: 8,
            border: "1px solid #334155",
            background: "#0f172a",
            zIndex: 200,
            maxHeight: 240,
            overflowY: "auto",
            boxShadow: "0 8px 24px rgba(0,0,0,0.55)",
          }}
        >
          {hasGroups
            ? groups.map((group) => (
                <div key={group.label ?? "__ungrouped"}>
                  {group.label && (
                    <div
                      aria-hidden="true"
                      style={{
                        padding: "6px 12px 3px",
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "#64748b",
                        userSelect: "none",
                        borderTop: groups.indexOf(group) > 0 ? "1px solid #1e293b" : "none",
                        marginTop: groups.indexOf(group) > 0 ? 4 : 0,
                      }}
                    >
                      {group.label}
                    </div>
                  )}
                  {group.items.map(({ option: o, idx }) => (
                    <button
                      key={o.value}
                      type="button"
                      role="option"
                      aria-selected={o.value === value}
                      tabIndex={idx === activeIndex ? 0 : -1}
                      onClick={() => { onChange(o.value); closeAndFocusTrigger(); }}
                      onKeyDown={(e) => handleOptionKeyDown(e, idx)}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onFocus={() => setOptionFocusedIndex(idx)}
                      onBlur={() => setOptionFocusedIndex(null)}
                      style={{
                        display: "block",
                        width: "100%",
                        minHeight: 44,
                        padding: "8px 12px",
                        fontSize: 13,
                        color: o.value === value ? "#93c5fd" : "#cbd5e1",
                        background: idx === activeIndex
                          ? (o.value === value ? "rgba(37,99,235,0.25)" : "rgba(37,99,235,0.10)")
                          : (o.value === value ? "rgba(37,99,235,0.15)" : "transparent"),
                        border: "none",
                        cursor: "pointer",
                        textAlign: "start",
                        fontWeight: o.value === value ? 500 : 400,
                        outline: optionFocusedIndex === idx ? "2px solid #2563eb" : "none",
                        outlineOffset: -2,
                      }}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              ))
            : options.map((o, idx) => (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={o.value === value}
                  tabIndex={idx === activeIndex ? 0 : -1}
                  onClick={() => { onChange(o.value); closeAndFocusTrigger(); }}
                  onKeyDown={(e) => handleOptionKeyDown(e, idx)}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onFocus={() => setOptionFocusedIndex(idx)}
                  onBlur={() => setOptionFocusedIndex(null)}
                  style={{
                    display: "block",
                    width: "100%",
                    minHeight: 44,
                    padding: "8px 12px",
                    fontSize: 13,
                    color: o.value === value ? "#93c5fd" : "#cbd5e1",
                    background: idx === activeIndex
                      ? (o.value === value ? "rgba(37,99,235,0.25)" : "rgba(37,99,235,0.10)")
                      : (o.value === value ? "rgba(37,99,235,0.15)" : "transparent"),
                    border: "none",
                    cursor: "pointer",
                    textAlign: "start",
                    fontWeight: o.value === value ? 500 : 400,
                    outline: optionFocusedIndex === idx ? "2px solid #2563eb" : "none",
                    outlineOffset: -2,
                  }}
                >
                  {o.label}
                </button>
              ))
          }
        </div>
      )}
    </div>
  );
}
